import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/solid';
import { Edge, Node, useReactFlow } from '@xyflow/react';
import { pick, isEqual } from 'lodash';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import ButtonAction from 'src/components/button-action';
import { WEB_UI_PATH } from 'src/config';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { NodeModel } from 'src/modules/network-configuration/models/node-model';
import { EEvents, ReloadValidateEvent } from 'src/types/events';
import { calculateNetworkAddress2 } from 'src/utils/ip';
import {
  validateIp,
  validateNode,
  INodeErrorMessage,
} from 'src/utils/validate';
import { twMerge } from 'tailwind-merge';

import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from '../../../models/node-data';
import EditDhcpServer from './edit-dhcp-server';
import EditGeneral from './edit-general';
import EditNotes from './edit-notes';
import EditPoE from './edit-poe';
import EditPorts from './edit-ports';
import EditPowerCable from './edit-power-cable';
import EditSTP from './edit-stp';
import { INodeEditData } from './type';

function validateDhcpIp(ip: string) {
  return validateIp(ip) ? ip : '';
}

function normalizeDhcpForEdit(dhcp: any) {
  const d = dhcp ?? {};
  return {
    leaseTime: d.leaseTime ?? 86400,
    poolEnabled: d.poolEnabled ?? false,
    poolStart: d.poolStart ? validateDhcpIp(d.poolStart) : '',
    poolEnd: d.poolEnd ? validateDhcpIp(d.poolEnd) : '',
    routerIp1: d.routerIp1 ? validateDhcpIp(d.routerIp1) : '',
    routerIp2: d.routerIp2 ? validateDhcpIp(d.routerIp2) : '',
    domainServerIp1: d.domainServerIp1 ? validateDhcpIp(d.domainServerIp1) : '',
    domainServerIp2: d.domainServerIp2 ? validateDhcpIp(d.domainServerIp2) : '',
    domainName: d.domainName ?? '',
    ntpServerIp1: d.ntpServerIp1 ? validateDhcpIp(d.ntpServerIp1) : '',
    ntpServerIp2: d.ntpServerIp2 ? validateDhcpIp(d.ntpServerIp2) : '',
  };
}

function categoryToEditValues(cat: NodeCategoryModel): INodeEditData {
  const interfaces = cat.ipInterfaces ?? [];
  return {
    name: cat.name ?? '',
    ip: cat.ip ?? '',
    subnetMask: cat.subnetMask ?? '',
    gateway: cat.gateway ?? '',
    dhcp: cat.dhcp ?? false,
    ipInterfaces: interfaces.map((iface) => ({
      ...iface,
      dhcpServer: iface.dhcpServer
        ? normalizeDhcpForEdit(iface.dhcpServer)
        : undefined,
    })),
    stp: cat.stp,
    poe: cat.poe,
    dhcpServer: normalizeDhcpForEdit(cat.dhcpServer),
    powerCableLength: cat.powerCableLength ?? 0,
    notes: cat.notes ?? '',
  };
}

interface StandardNodeDialogEditProps {
  id: string;
  open: boolean;
  labelKey: string;
  data: NodeCategoryModel;
  onClose: () => void;
  onSaved: (newData: Partial<NodeCategoryModel>, closeDialog?: boolean) => void;
}

const TAB_FIELDS_NODE: Record<string, (keyof INodeEditData)[]> = {
  ip: ['name', 'ip', 'subnetMask', 'gateway', 'dhcp', 'ipInterfaces'],
  'power-cable': ['powerCableLength'],
  stp: ['stp'],
  'dhcp-server': ['dhcpServer', 'ipInterfaces'],
  poe: ['poe'],
  notes: ['notes'],
};

export default function StandardNodeDialogEdit({
  id,
  open,
  labelKey,
  data,
  onClose,
  onSaved,
}: StandardNodeDialogEditProps) {
  const { useCustomVlan, globalIpSubnet } = useGlobalContext();
  const [, setForceRefresh] = useState<number>(0);

  const tabs = useMemo(
    () => [
      {
        key: 'ip',
        name: data.switchType?.includes(EDataSwitchType.MANAGED)
          ? 'IP Settings'
          : 'General Settings',
      },
      ...(data.type === EDataType.Switch
        ? [{ key: 'power-cable', name: 'Power Cable' }]
        : []),
      ...(data.type === EDataType.Switch &&
      data.switchType?.includes(EDataSwitchType.MANAGED)
        ? [
            { key: 'stp', name: 'STP' },
            { key: 'dhcp-server', name: 'DHCP Server' },
          ]
        : []),
      ...(data.type === EDataType.Device
        ? [{ key: 'poe', name: 'PoE Settings' }]
        : []),
      { key: 'notes', name: 'Notes' },
    ],
    [data.type, data.switchType],
  );

  const [currentTab, setCurrentTab] = useState(tabs[0].key);
  const { getNodes, getEdges } = useReactFlow();
  const [values, setValues] = useState<INodeEditData>(
    categoryToEditValues(data),
  );
  const [savedValues, setSavedValues] = useState<INodeEditData>(
    categoryToEditValues(data),
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  const [errors, setErrors] = useState<INodeErrorMessage>({
    name: '',
    ip: '',
    subnet: '',
    subnetMask: '',
    gateway: '',
    ipInterfaces: [],
    stp: {},
    dhcpServer: {},
  });

  const isTabDirty = useCallback(
    (tab: string) => {
      const fields = TAB_FIELDS_NODE[tab];
      if (!fields) return false;
      const currentTabData = pick(values, fields);
      const savedTabData = pick(savedValues, fields);
      return !isEqual(currentTabData, savedTabData);
    },
    [values, savedValues],
  );

  const handleSave = useCallback(
    (closeDialog = true) => {
      const fields = TAB_FIELDS_NODE[currentTab];
      const dataToSave: any = pick(values, fields);

      if (currentTab === 'ip') {
        if (!data.ipInterfaces || data.ipInterfaces.length === 0) {
          dataToSave.subnet = calculateNetworkAddress2(
            values.ip,
            values.subnetMask,
          );
        }
      }

      // Normalize ipInterfaces if present (shared by ip and dhcp-server tabs)
      if (dataToSave.ipInterfaces) {
        dataToSave.ipInterfaces = dataToSave.ipInterfaces.map((iface: any) => ({
          ...iface,
          dhcpServer: iface.dhcpServer
            ? normalizeDhcpForEdit(iface.dhcpServer)
            : undefined,
        }));
      }

      // Normalize top-level dhcpServer if present
      if (dataToSave.dhcpServer) {
        dataToSave.dhcpServer = normalizeDhcpForEdit(dataToSave.dhcpServer);
      }

      onSaved(dataToSave, closeDialog);
      setSavedValues((prev) => ({ ...prev, ...pick(values, fields) }));
      toast.success('Settings saved successfully');
      setForceRefresh((prev) => prev + 1);

      const reloadAfterSave = () => {
        const nodes = getNodes();
        const node = nodes.find((n) => n.id === id);
        const cat = node?.data?.category as NodeCategoryModel | undefined;
        if (cat) {
          const newValues = categoryToEditValues(cat);
          setValues(newValues);
          setSavedValues(newValues);
          if (cat.validationErrors) {
            setErrors(cat.validationErrors);
          }
        }
      };
      setTimeout(reloadAfterSave, 150);
    },
    [currentTab, values, onSaved, data, id, getNodes],
  );

  const handleCloseAttempt = useCallback(() => {
    if (isTabDirty(currentTab)) {
      setIsClosing(true);
      setShowConfirmation(true);
    } else {
      onClose();
    }
  }, [currentTab, isTabDirty, onClose]);

  const handleTabChange = (tabKey: string) => {
    if (tabKey === currentTab) return;

    if (isTabDirty(currentTab)) {
      setPendingTab(tabKey);
      setShowConfirmation(true);
    } else {
      setCurrentTab(tabKey);
    }
  };

  const handleConfirmApply = () => {
    handleSave(false);
    setShowConfirmation(false);
    if (isClosing) {
      onClose();
    } else if (pendingTab) {
      setCurrentTab(pendingTab);
      setPendingTab(null);
    }
    setIsClosing(false);
  };

  const handleConfirmDismiss = () => {
    const fields = TAB_FIELDS_NODE[currentTab];
    const dataToRevert = pick(savedValues, fields);
    setValues((prev) => ({ ...prev, ...dataToRevert }));
    setShowConfirmation(false);
    if (isClosing) {
      onClose();
    } else if (pendingTab) {
      setCurrentTab(pendingTab);
      setPendingTab(null);
    }
    setIsClosing(false);
  };

  const handleConfirmCancel = () => {
    setShowConfirmation(false);
    setPendingTab(null);
    setIsClosing(false);
  };

  useEffect(() => {
    const initialValues = categoryToEditValues(data);
    setValues(initialValues);
    setSavedValues(initialValues);
  }, [data]);

  useEffect(() => {
    if (!open) {
      setErrors({
        name: '',
        ip: '',
        subnet: '',
        subnetMask: '',
        gateway: '',
        ipInterfaces: [],
        stp: {},
        dhcpServer: {},
      });
      setCurrentTab(tabs[0].key);
    }
  }, [open, tabs]);

  const handleValidate = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    const result = validateNode(
      id,
      {
        ...data,
        name: values.name,
        ip: values.ip,
        subnetMask: values.subnetMask,
        gateway: values.gateway,
        ipInterfaces: values.ipInterfaces,
        stp: values.stp,
        dhcpServer: values.dhcpServer ?? data.dhcpServer,
      },
      nodes as Node<NodeModel>[],
      edges as Edge[],
      useCustomVlan ? '' : (globalIpSubnet?.ipSubnet ?? ''),
      true,
      useCustomVlan ? undefined : globalIpSubnet?.subnetMask,
    );
    setErrors(result.errorMessage);
  }, [id, data, values, getNodes, getEdges, useCustomVlan, globalIpSubnet]);

  useEffect(() => {
    handleValidate();
  }, [values, handleValidate]);

  const isManaged = useMemo(() => {
    if (data.type === EDataType.Device) return true;
    return data.switchType?.includes(EDataSwitchType.MANAGED) || false;
  }, [data.switchType, data.type]);

  useEffect(() => {
    const handleReloadValidate = (event: CustomEvent<ReloadValidateEvent>) => {
      if (event.detail.nodeIds.includes(id)) {
        handleValidate();
      }
    };
    window.addEventListener(
      EEvents.RELOAD_VALIDATE,
      handleReloadValidate as EventListener,
    );
    return () => {
      window.removeEventListener(
        EEvents.RELOAD_VALIDATE,
        handleReloadValidate as EventListener,
      );
    };
  }, [id, handleValidate]);

  if (!open) return null;
  return (
    <>
      <Dialog
        open
        onClose={handleCloseAttempt}
        className="relative z-50"
        data-testid={`node-edit-dialog-${id}`}
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              data-testid={`node-edit-dialog-panel-${id}`}
              className={twMerge([
                'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
                'min-w-[400px]',
              ])}
            >
              <div className="absolute right-2 top-2 flex items-center justify-center gap-2">
                <div className="flex items-center justify-center gap-2">
                  {isManaged && (
                    <Button
                      size="sm"
                      color="green"
                      data-tooltip-id="app-tooltip"
                      data-tooltip-content="Open details"
                      type="button"
                      onClick={() => {
                        window.open(`/${WEB_UI_PATH}/${id}`, '_self')?.focus();
                      }}
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </Button>
                  )}
                  <span className="text-base font-bold text-gray-500">
                    {labelKey}
                  </span>
                </div>
                <button
                  type="button"
                  data-testid={`node-edit-close-button-${id}`}
                  onClick={handleCloseAttempt}
                  className="h-6 w-6 rounded-full p-1 text-gray-500 hover:bg-gray-100"
                >
                  <XMarkIcon />
                </button>
              </div>

              <div>
                <div className="mt-3 text-left">
                  <div className="relative border-b border-blue-400 pb-0.5">
                    <nav
                      aria-label="Tabs"
                      className="-mb-px flex space-x-4 overflow-x-auto"
                    >
                      {tabs.map((tab) => (
                        <button
                          key={tab.name}
                          type="button"
                          data-testid={`node-edit-tab-button-${id}-${tab.key}`}
                          aria-current={
                            currentTab === tab.key ? 'page' : undefined
                          }
                          className={twMerge([
                            'text-md whitespace-nowrap border-b-2 px-2 py-1 font-medium',
                            currentTab === tab.key
                              ? 'border-blue-400 bg-blue-400 text-white'
                              : 'border-transparent text-gray-500',
                          ])}
                          onClick={() => handleTabChange(tab.key)}
                        >
                          {tab.name}
                        </button>
                      ))}
                    </nav>
                  </div>
                  {currentTab === 'ip' && (
                    <EditGeneral
                      id={id}
                      values={values}
                      setValues={setValues}
                      errors={errors}
                      type={data.type}
                      ipInterfaces={data.ipInterfaces || []}
                      isManaged={isManaged}
                    />
                  )}
                  {currentTab === 'ports' && (
                    <EditPorts
                      values={values}
                      setValues={setValues}
                      errors={errors}
                      type={data.type}
                    />
                  )}
                  {currentTab === 'poe' && (
                    <EditPoE
                      id={id}
                      values={{ poe: values.poe }}
                      setValues={(updater) =>
                        setValues((prev) => ({
                          ...prev,
                          ...(typeof updater === 'function'
                            ? (updater({
                                poe: prev.poe,
                              }) as Partial<INodeEditData>)
                            : (updater as Partial<INodeEditData>)),
                        }))
                      }
                    />
                  )}
                  {currentTab === 'power-cable' && (
                    <EditPowerCable
                      values={{ powerCableLength: values.powerCableLength }}
                      setValues={(updater) =>
                        setValues((prev) => ({
                          ...prev,
                          ...(typeof updater === 'function'
                            ? (updater({
                                powerCableLength: prev.powerCableLength,
                              }) as Partial<INodeEditData>)
                            : (updater as Partial<INodeEditData>)),
                        }))
                      }
                    />
                  )}
                  {currentTab === 'stp' && (
                    <EditSTP
                      id={id}
                      values={{ stp: values.stp }}
                      setValues={(updater) =>
                        setValues((prev) => ({
                          ...prev,
                          ...(typeof updater === 'function'
                            ? (updater({
                                stp: prev.stp,
                              }) as Partial<INodeEditData>)
                            : (updater as Partial<INodeEditData>)),
                        }))
                      }
                      errors={errors.stp}
                    />
                  )}
                  {currentTab === 'dhcp-server' && (
                    <EditDhcpServer
                      id={id}
                      data={data}
                      values={values}
                      setValues={setValues}
                      errors={errors.dhcpServer}
                    />
                  )}
                  {currentTab === 'notes' && (
                    <EditNotes
                      values={{ notes: values.notes }}
                      setValues={(updater) =>
                        setValues((prev) => ({
                          ...prev,
                          ...(typeof updater === 'function'
                            ? (updater({
                                notes: prev.notes,
                              }) as Partial<INodeEditData>)
                            : (updater as Partial<INodeEditData>)),
                        }))
                      }
                    />
                  )}
                </div>
                <div className="mt-5 w-full sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <ButtonAction
                    type="button"
                    color="blue"
                    variant="contained"
                    onClick={() => handleSave(true)}
                    fullWidth
                    className="sm:col-start-2"
                    data-testid={`node-edit-general-save-button-${id}`}
                  >
                    Save
                  </ButtonAction>
                  <ButtonAction
                    type="button"
                    color="info"
                    variant="outlined"
                    onClick={handleCloseAttempt}
                    fullWidth
                    className="mt-3 sm:col-start-1 sm:mt-0"
                    data-testid={`node-edit-general-cancel-button-${id}`}
                  >
                    Cancel
                  </ButtonAction>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Unsaved changes confirmation dialog */}
      <Dialog
        open={showConfirmation}
        onClose={handleConfirmCancel}
        className="relative z-[60]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:size-10">
                  <ExclamationTriangleIcon
                    className="size-6 text-yellow-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold text-gray-900"
                  >
                    Unsaved changes
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You have unsaved changes on the current tab. Would you
                      like to apply them or dismiss them?
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
                <ButtonAction
                  type="button"
                  color="blue"
                  variant="contained"
                  onClick={handleConfirmApply}
                  className="w-full sm:w-auto"
                >
                  Apply
                </ButtonAction>
                <ButtonAction
                  type="button"
                  color="danger"
                  variant="contained"
                  onClick={handleConfirmDismiss}
                  className="mt-3 w-full sm:mt-0 sm:w-auto"
                >
                  Dismiss
                </ButtonAction>
                <ButtonAction
                  type="button"
                  color="info"
                  variant="outlined"
                  onClick={handleConfirmCancel}
                  className="mt-3 w-full sm:mt-0 sm:w-auto"
                >
                  Cancel
                </ButtonAction>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}
